<template>
  <div
    ref="editorContainer"
    class="notebook-cell-editor w-full font-mono text-xs"
    :class="{ 'opacity-70 pointer-events-none': disabled }"
  />
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { EditorState, Prec } from '@codemirror/state';
import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLineGutter,
  highlightActiveLine,
  drawSelection,
} from '@codemirror/view';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from '@codemirror/commands';
import { python } from '@codemirror/lang-python';
import { oneDark } from '@codemirror/theme-one-dark';
import {
  syntaxHighlighting,
  defaultHighlightStyle,
  bracketMatching,
  foldGutter,
  foldKeymap,
} from '@codemirror/language';
import { closeBrackets, closeBracketsKeymap, autocompletion, completionKeymap } from '@codemirror/autocomplete';

const props = defineProps<{
  modelValue: string;
  language?: 'python' | 'markdown';
  disabled?: boolean;
  minHeight?: number;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'run'): void;
  (e: 'focus'): void;
}>();

const editorContainer = ref<HTMLDivElement | null>(null);
let editorView: EditorView | null = null;
let suppressUpdate = false;

function buildExtensions() {
  const lang = props.language === 'markdown' ? [] : [python()];

  return [
    lineNumbers(),
    highlightActiveLineGutter(),
    highlightActiveLine(),
    drawSelection(),
    history(),
    foldGutter(),
    bracketMatching(),
    closeBrackets(),
    autocompletion(),
    ...lang,
    syntaxHighlighting(defaultHighlightStyle, { fallback: true }),
    oneDark,
    // Prec.highest so Shift-Enter runs the cell instead of defaultKeymap's
    // { key: "Enter", shift: insertNewlineAndIndent }.
    Prec.highest(
      keymap.of([
        {
          key: 'Shift-Enter',
          run: () => {
            emit('run');
            return true;
          },
        },
      ])
    ),
    keymap.of([
      indentWithTab,
      ...closeBracketsKeymap,
      ...defaultKeymap,
      ...historyKeymap,
      ...foldKeymap,
      ...completionKeymap,
    ]),
    EditorView.updateListener.of((update) => {
      if (suppressUpdate) return;
      if (update.docChanged) {
        emit('update:modelValue', update.state.doc.toString());
      }
      if (update.focusChanged && update.view.hasFocus) {
        emit('focus');
      }
    }),
    EditorView.theme({
      '&': {
        height: 'auto',
        minHeight: `${props.minHeight ?? 96}px`,
        fontSize: '12.5px',
        backgroundColor: '#0d1321',
      },
      '.cm-scroller': {
        overflow: 'auto',
        maxHeight: '420px',
        fontFamily: 'Fira Code, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        lineHeight: '1.55',
      },
      '.cm-content': {
        padding: '8px 0',
        caretColor: '#fbbf24',
      },
      '.cm-gutters': {
        backgroundColor: '#090d16',
        borderColor: '#1e293b',
        color: '#64748b',
        minHeight: `${props.minHeight ?? 96}px`,
      },
      '.cm-activeLine': { backgroundColor: '#1e293b66' },
      '.cm-activeLineGutter': { backgroundColor: '#1e293b99' },
      '&.cm-focused': { outline: 'none' },
      '.cm-selectionBackground, &.cm-focused .cm-selectionBackground': {
        backgroundColor: '#334155aa !important',
      },
    }),
    EditorState.tabSize.of(4),
  ];
}

onMounted(() => {
  if (!editorContainer.value) return;

  editorView = new EditorView({
    state: EditorState.create({
      doc: props.modelValue || '',
      extensions: buildExtensions(),
    }),
    parent: editorContainer.value,
  });
});

watch(
  () => props.modelValue,
  (newValue) => {
    if (!editorView) return;
    const current = editorView.state.doc.toString();
    if (newValue === current) return;
    suppressUpdate = true;
    editorView.dispatch({
      changes: { from: 0, to: editorView.state.doc.length, insert: newValue || '' },
    });
    suppressUpdate = false;
  }
);

onBeforeUnmount(() => {
  editorView?.destroy();
  editorView = null;
});

defineExpose({
  focus: () => editorView?.focus(),
});
</script>

<style scoped>
.notebook-cell-editor :deep(.cm-editor) {
  border-radius: 0;
}
</style>
